import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, errorMessage, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { InvoiceTable } from "@/components/InvoiceTable";
import { InvoiceCardList } from "@/components/InvoiceCardList";
import { PeriodPicker } from "@/components/PeriodPicker";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { STATUS_TABS, SOURCES } from "@/lib/constants";
import { formatKr, todayIso } from "@/lib/format";
import { toast } from "sonner";
import { Download, FileText, Search, X, Upload, SlidersHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

export default function InvoicesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { canWrite, isOwner, actAs } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [counts, setCounts] = useState(null);
  const [qInput, setQInput] = useState(params.get("q") || "");
  const [payTarget, setPayTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const status = params.get("status") || "alle";
  const q = params.get("q") || "";
  const category = params.get("category_id") || "";
  const supplier = params.get("supplier_id") || "";
  const source = params.get("source") || "";
  const dateFrom = params.get("date_from") || "";
  const dateTo = params.get("date_to") || "";
  const sort = params.get("sort") || "due_date";
  const order = params.get("order") || "asc";
  const page = Number(params.get("page") || 1);

  const setParam = useCallback((key, value) => {
    const next = new URLSearchParams(params);
    if (value === "" || value === null || value === undefined || value === "alle") next.delete(key); else next.set(key, value);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  }, [params, setParams]);

  const setPeriod = (from, to) => {
    const next = new URLSearchParams(params);
    if (from) next.set("date_from", from); else next.delete("date_from");
    if (to) next.set("date_to", to); else next.delete("date_to");
    next.delete("page");
    setParams(next, { replace: true });
  };

  const loadCounts = useCallback(() => {
    api.get("/dashboard").then((r) => setCounts(r.data.kpis)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.items)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data.items)).catch(() => {});
    loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    const t = setTimeout(() => { if (qInput !== q) setParam("q", qInput.trim()); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qInput]);

  useEffect(() => { setQInput(q); }, [q]);

  const queryParams = useMemo(() => ({
    status: status !== "alle" ? status : undefined,
    q: q || undefined,
    category_id: category || undefined,
    supplier_id: supplier || undefined,
    source: source || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    sort, order, page, limit: 50,
  }), [status, q, category, supplier, source, dateFrom, dateTo, sort, order, page]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/invoices", { params: queryParams });
      setData(res.data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c])), [categories]);
  const activeFilterCount = [q, category, supplier, source, dateFrom || dateTo].filter(Boolean).length;
  const hasFilters = activeFilterCount > 0;

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (status !== "alle") next.set("status", status);
    setParams(next, { replace: true });
    setQInput("");
  };

  const onSort = (field) => {
    if (sort === field) setParam("order", order === "asc" ? "desc" : "asc");
    else {
      const next = new URLSearchParams(params);
      next.set("sort", field);
      next.set("order", field === "amount" || field === "created_at" ? "desc" : "asc");
      setParams(next, { replace: true });
    }
  };

  const confirmPay = async () => {
    if (!payTarget) return;
    try {
      await api.post(`/invoices/${payTarget.id}/pay`, { paid_at: todayIso() });
      toast.success("Faktura markert som betalt");
      setPayTarget(null);
      load();
      loadCounts();
      window.dispatchEvent(new Event("rp:refresh-notifications"));
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success("Faktura slettet");
      setDeleteTarget(null);
      load();
      loadCounts();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const exportCsv = async () => {
    try {
      await downloadFile("/invoices/export.csv", `fakturaer_${todayIso()}.csv`, { ...queryParams, page: undefined, limit: undefined, sort: undefined, order: undefined });
      toast.success("CSV-fil lastet ned");
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke eksportere"));
    }
  };

  const tabCount = (v) => (counts && v !== "alle" && counts[v] ? counts[v].count : null);
  const totalSum = data?.items?.reduce((a, i) => a + (i.amount || 0), 0) || 0;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  const FilterFields = (
    <>
      <Select value={category || "alle"} onValueChange={(v) => setParam("category_id", v)}>
        <SelectTrigger data-testid="invoice-category-select" aria-label="Kategori"><SelectValue placeholder="Kategori" /></SelectTrigger>
        <SelectContent className="bg-card">
          <SelectItem value="alle">Alle kategorier</SelectItem>
          <SelectItem value="ingen">Uten kategori</SelectItem>
          {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={supplier || "alle"} onValueChange={(v) => setParam("supplier_id", v)}>
        <SelectTrigger data-testid="invoice-supplier-select" aria-label="Leverandør"><SelectValue placeholder="Leverandør" /></SelectTrigger>
        <SelectContent className="bg-card">
          <SelectItem value="alle">Alle leverandører</SelectItem>
          {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={source || "alle"} onValueChange={(v) => setParam("source", v)}>
        <SelectTrigger data-testid="invoice-source-select" aria-label="Kilde"><SelectValue placeholder="Kilde" /></SelectTrigger>
        <SelectContent className="bg-card">
          <SelectItem value="alle">Alle kilder</SelectItem>
          {Object.entries(SOURCES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
      <PeriodPicker from={dateFrom} to={dateTo} onChange={setPeriod} />
    </>
  );

  return (
    <div className="space-y-5 md:space-y-6">
      <PageHeader
        title="Fakturaer"
        description={actAs ? `Regningene til ${actAs.owner_name} – søk, filtrer${canWrite ? " og håndter" : ""}.` : "Alle regningene dine – søk, filtrer og håndter."}
        actions={
          <>
            <Button variant="outline" onClick={exportCsv} data-testid="invoice-export-csv-button"><Download className="h-4 w-4 mr-2" /> Eksporter CSV</Button>
            {canWrite ? <Button onClick={() => navigate("/last-opp")} className="hidden sm:inline-flex" data-testid="invoices-upload-button"><Upload className="h-4 w-4 mr-2" /> Last opp</Button> : null}
          </>
        }
      />

      <Tabs value={status} onValueChange={(v) => setParam("status", v)}>
        <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
          <TabsList className="h-auto inline-flex w-max sm:w-auto sm:flex-wrap bg-card border p-1">
            {STATUS_TABS.map((t) => {
              const c = tabCount(t.value);
              return (
                <TabsTrigger key={t.value} value={t.value} className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-3 py-2 gap-1.5" data-testid={`invoice-tab-${t.value}`}>
                  {t.label}
                  {c ? <span className="rounded-full bg-secondary px-1.5 text-[11px] tnum text-muted-foreground group-data-[state=active]:bg-card">{c}</span> : null}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>
      </Tabs>

      {/* Filterrad */}
      <div className="rounded-xl border bg-card p-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Søk leverandør, KID, fakturanr, beløp…" className="pl-9" data-testid="invoice-search-input" aria-label="Søk i fakturaer" />
          </div>
          <Button variant="outline" className="md:hidden shrink-0" onClick={() => setFiltersOpen((o) => !o)} aria-expanded={filtersOpen} data-testid="invoice-filters-toggle">
            <SlidersHorizontal className="h-4 w-4 mr-2" /> Filter{activeFilterCount ? <span className="ml-1.5 rounded-full bg-primary text-primary-foreground px-1.5 text-[11px] tnum">{activeFilterCount}</span> : null}
          </Button>
          <div className="hidden md:grid grid-cols-4 gap-2 flex-[2]">{FilterFields}</div>
          <Button variant="ghost" className={cn("hidden md:inline-flex shrink-0", !hasFilters && "invisible")} onClick={resetFilters} data-testid="invoice-filters-reset-button"><X className="h-4 w-4 mr-1" /> Nullstill</Button>
        </div>
        <Collapsible open={filtersOpen} className="md:hidden">
          <CollapsibleContent>
            <div className="grid gap-2 pt-3">
              {FilterFields}
              {hasFilters ? <Button variant="ghost" onClick={resetFilters} data-testid="invoice-filters-reset-mobile"><X className="h-4 w-4 mr-1" /> Nullstill filter</Button> : null}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {loading && !data ? (
        <div className="space-y-2">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : data?.items?.length ? (
        <>
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span data-testid="invoice-result-count">{data.total} {data.total === 1 ? "faktura" : "fakturaer"}{hasFilters ? " (filtrert)" : ""}</span>
            <span className="tnum">Sum: <strong className="text-foreground">{formatKr(totalSum)}</strong></span>
          </div>
          <div className="hidden md:block">
            <InvoiceTable items={data.items} categories={catMap} onMarkPaid={canWrite ? setPayTarget : undefined} onDelete={isOwner ? setDeleteTarget : undefined} sort={sort} order={order} onSort={onSort} />
          </div>
          <div className="md:hidden">
            <InvoiceCardList items={data.items} categories={catMap} />
          </div>
          {totalPages > 1 ? (
            <div className="flex items-center justify-center gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} data-testid="invoice-prev-page">Forrige</Button>
              <span className="text-sm text-muted-foreground tnum">Side {page} av {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setParam("page", String(page + 1))} data-testid="invoice-next-page">Neste</Button>
            </div>
          ) : null}
        </>
      ) : (
        <EmptyState icon={FileText} title={hasFilters || status !== "alle" ? "Ingen fakturaer passer filteret" : "Ingen fakturaer her ennå"} description={hasFilters || status !== "alle" ? "Prøv å justere søk eller filter." : canWrite ? "Last opp en PDF, ta et bilde eller videresend en e-post for å komme i gang." : "Det er ingen registrerte regninger på denne kontoen."} actionLabel={hasFilters ? "Nullstill filter" : canWrite ? "Last opp" : undefined} onAction={hasFilters ? resetFilters : undefined} actionTo={hasFilters || !canWrite ? undefined : "/last-opp"} testId="invoices-empty-state" />
      )}

      <AlertDialog open={!!payTarget} onOpenChange={(o) => !o && setPayTarget(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Marker som betalt?</AlertDialogTitle>
            <AlertDialogDescription>{payTarget?.issuer_name} – {formatKr(payTarget?.amount, payTarget?.currency)}. Fakturaen flyttes til «Betalt» med dagens dato.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPay} data-testid="confirm-mark-paid-button">Ja, marker som betalt</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette fakturaen?</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.issuer_name} – {formatKr(deleteTarget?.amount, deleteTarget?.currency)}. Dokumentet slettes permanent. Dette kan ikke angres.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={confirmDelete} data-testid="confirm-delete-button">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
